import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { useDndGridInternalContext } from './context';
import { DropType, IColumnDropPosition, IGridPosition } from './types';
import { GridVerticalBorder } from './GridVerticalBorder';
import styled from 'styled-components';
import { useDndContext } from '@dnd-kit/core';
import { useRowColumnResizerContext } from './useRowColumnResizer';
import { MaybeNil } from '../../utils/interfaces';

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
`;

const OverlayText = styled.div`
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.8);
  font-size: 14px;
  color: #fff;
`;

const Cell = styled.div`
  position: relative;
  @media print {
    break-inside: avoid;
  }
`;

interface IDndGridCellProps extends IGridPosition {
  id: string;
  colSpan: MaybeNil<number>;
}

export const DndGridCell = ({
  children,
  rowIndex,
  colIndex,
  id,
  colSpan,
}: PropsWithChildren<IDndGridCellProps>) => {
  const { setCurrentDragHandle, disabled } = useDndGridInternalContext();
  const { active } = useDndContext(); // note that this dndkit's context
  const { registerColumn, columnsWidthInPixels } = useRowColumnResizerContext();

  // register this cell as resizable column in the current row
  // the registerColumn returns an un-register function when registration
  // properties (colspan or position in the grid) change or component unmounts
  useEffect(
    () => registerColumn(rowIndex, colIndex, colSpan),
    [colSpan, colIndex, registerColumn, rowIndex],
  );

  const leftDropPosition = useMemo<IColumnDropPosition>(
    () => ({ type: DropType.COLUMN, rowIndex, colIndex }),
    [colIndex, rowIndex],
  );

  const rightDropPosition = useMemo<IColumnDropPosition>(
    () => ({ type: DropType.COLUMN, rowIndex, colIndex: colIndex + 1 }),
    [colIndex, rowIndex],
  );

  return (
    <>
      {colIndex === 0 && <GridVerticalBorder position={leftDropPosition} />}
      <Cell
        onMouseEnter={() => setCurrentDragHandle(disabled ? null : id)}
        onMouseLeave={() => setCurrentDragHandle(null)}
      >
        <div
          style={{
            width: columnsWidthInPixels[colIndex] ?? undefined,
            height: '100%',
          }}
        >
          {children}
        </div>
        {!disabled && id === active?.id && (
          <Overlay>
            <OverlayText>Moving this item to a new location…</OverlayText>
          </Overlay>
        )}
      </Cell>
      <GridVerticalBorder position={rightDropPosition} />
    </>
  );
};
